// Frontend analytics helpers. Single beacon endpoint; the server fills
// in country, IP hash, anon cookie, and signed-in user id automatically.

export type EventKind =
  | "pageview"
  | "signup_started"
  | "demo_start"
  | "cta_click"
  | "contact";

interface TrackOpts {
  path?: string;
  referrer?: string;
  props?: Record<string, unknown>;
}

// sendBeacon when available (survives navigations, async / non-blocking),
// fall back to fetch with keepalive.
function send(payload: unknown) {
  try {
    const body = JSON.stringify(payload);
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      // sendBeacon requires a Blob with a JSON content-type so the worker
      // can JSON.parse the body — a raw string trips content-type sniffing.
      const ok = navigator.sendBeacon(
        "/api/track",
        new Blob([body], { type: "application/json" }),
      );
      if (ok) return;
    }
    void fetch("/api/track", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      credentials: "include",
      keepalive: true,
    }).catch(() => {
      /* swallow — analytics is best-effort */
    });
  } catch {
    /* swallow */
  }
}

export function trackEvent(kind: EventKind, opts: TrackOpts = {}) {
  send({
    kind,
    path: opts.path ?? (typeof location !== "undefined" ? location.pathname + location.search : ""),
    referrer:
      opts.referrer ??
      (typeof document !== "undefined" ? document.referrer || undefined : undefined),
    props: opts.props,
  });
}

export function trackPageview(path: string) {
  // Strip query strings that don't matter (utm_* params are kept; everything
  // else gets dropped to avoid leaking PII into the analytics table).
  send({
    kind: "pageview",
    path,
    referrer:
      typeof document !== "undefined" ? document.referrer || undefined : undefined,
  });
}
